import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FONT } from "@plannel/components/ai/typographyTokens";
import AiSegmentedControl from "@plannel/pages/ai/AiSegmentedControl";
import ProviderChip from "./ProviderChip";
import {
  CARD_SX, NUM_SX, UC, baseCostText, chart, featureLabel, fmtBase, fmtCost, fmtDec1, fmtInt, operationLabel, num, userLabel,
  moduleLabel,
} from "./llmUsageUtil";

const TOP_N = 10;
// 수치 열 폭 — 6개 언어 머리글(큰 글자 12px, 정렬 표시 포함)이 잘리지 않는 최소값. 이름 열은 남는 폭
// 총 토큰은 억 단위(11자리 + 쉼표)까지 가서 비용 열 폭을 나눠 받는다 — 이름 열 폭은 그대로
const COLS = "minmax(0, 1fr) 84px 108px 112px";

// 축 전환은 받은 요약 응답 안에서만 — 재조회하지 않는다
export const BREAKDOWN_AXES = [
  { key: "feature", labelKey: "llmUsageByFeature", headerKey: "llmUsageFeature", rows: "by_feature",
    name: (t, r) => featureLabel(t, r.feature_cd), muted: (r) => !r.feature_cd || r.feature_cd === "UNATTRIBUTED" },
  { key: "module", labelKey: "llmUsageByModule", headerKey: "agentTraceModule", rows: "by_module",
    name: (t, r) => moduleLabel(t, r.module_cd), muted: (r) => !r.module_cd || r.module_cd === "-" },
  { key: "user", labelKey: "llmUsageByUser", headerKey: "user", rows: "by_user",
    name: (t, r) => userLabel(t, r.user_id, r.user_name), muted: (r) => r.user_id == null },
  { key: "model", labelKey: "llmUsageByModel", headerKey: "agentTraceMetricModel", rows: "by_model",
    // 표시명(단가 마스터)이 있으면 이름, 없으면 모델 ID
    name: (t, r) => r.model_nm || r.model_id || t("llmUsageNotSet"), muted: (r) => !r.model_id },
];

const CELL_NUM_SX = { ...NUM_SX, textAlign: "right", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const ROW_SX = { display: "grid", gridTemplateColumns: COLS, columnGap: "14px", alignItems: "center", px: "16px" };
// 헤더·본문·합계의 열이 스크롤바 유무로 어긋나지 않게 세 구역 모두 거터를 예약한다
const SECTION_SX = { display: "block", scrollbarGutter: "stable", flexShrink: 0, overflow: "hidden" };

const ShareBar = ({ pct }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: "5px" }}>
    <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: chart.barTrack, overflow: "hidden" }}>
      <Box sx={{ width: `${Math.min(100, Math.max(0, pct))}%`, height: "100%", bgcolor: chart.barFill, borderRadius: 2 }} />
    </Box>
    <Box sx={{ ...NUM_SX, width: 38, textAlign: "right", fontSize: FONT.CAPTION, color: UC.text3 }}>{`${fmtDec1(pct)}%`}</Box>
  </Box>
);

const ELLIPSIS_SX = { maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" };

// 비용 셀 — 기본 통화 환산액이 있으면 둘째 줄에 작게 덧붙인다(주 표시는 단가 통화 그대로)
const CostStack = ({ main, base }) => {
  if (!base) return main;
  return (
    <Box component="span" sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 0 }}>
      <Box component="span" sx={ELLIPSIS_SX}>{main}</Box>
      <Box component="span" sx={{ ...ELLIPSIS_SX, fontSize: FONT.CAPTION, fontWeight: 400, color: UC.text3 }}>{base}</Box>
    </Box>
  );
};

const LlmUsageBreakdownCard = ({ summary, axis, onChangeAxis, expanded, onExpand, sx }) => {
  const { t } = useTranslation();
  const def = BREAKDOWN_AXES.find((a) => a.key === axis) || BREAKDOWN_AXES[0];
  const dense = def.key === "user";
  const k = summary.kpi || {};
  const kpiCosts = k.costs || [];
  // 통화가 하나일 때만 비용을 숫자로 비교·합산한다. 여럿이면 토큰을 기준으로 삼는다
  const currency = kpiCosts.length === 1 ? kpiCosts[0].currency_cd : null;
  const basisKey = currency ? "cost" : "tokens";
  const [sort, setSort] = useState(null);
  const sortKey = sort?.key || basisKey;
  const sortDesc = sort ? sort.desc : true;

  const costOf = (costs) => (currency ? num((costs || []).find((c) => c.currency_cd === currency)?.cost_amt) : null);
  const costsText = (costs) => (costs || []).map((c) => fmtBase(c.cost_amt, c.currency_cd)).join(" / ");
  const baseCurrency = summary.base_currency_cd;

  const rows = useMemo(() => {
    const src = summary[def.rows] || [];
    // 모델 축: 같은 표시명이 여러 제공사에 걸치면 제공사 칩을 붙인다. 같은 제공사 안에서 겹치면
    //   같은 모델(작업 유형만 다름) → 작업 유형, 다른 모델(이름만 같음) → 모델 ID 를 덧붙인다
    const count = {};
    const providersOf = {};
    const bump = (k) => { count[k] = (count[k] || 0) + 1; };
    const SEP = String.fromCharCode(0);   // 표시명·제공사·ID 를 이어 붙일 때 겹치지 않는 구분자
    if (def.key === "model") {
      src.forEach((r) => {
        const label = def.name(t, r);
        (providersOf[label] = providersOf[label] || new Set()).add(r.provider_cd);
        bump([label, r.provider_cd].join(SEP));
        bump([label, r.provider_cd, r.model_id].join(SEP));
      });
    }
    return src.map((r, i) => {
      const base = def.name(t, r);
      let name = base;
      if (count[[base, r.provider_cd].join(SEP)] > 1) {
        const sameModel = count[[base, r.provider_cd, r.model_id].join(SEP)] > 1;
        name = sameModel ? `${base} · ${operationLabel(t, r.operation_cd)}` : `${base} · ${r.model_id}`;
      }
      return {
        id: `${i}:${name}:${r.provider_cd || ""}`, name, muted: def.muted(r),
        provider: (providersOf[base]?.size || 0) > 1 ? r.provider_cd : null,
        calls: num(r.call_cnt), tokens: num(r.total_tokens), cost: costOf(r.costs),
        costText: currency ? null : costsText(r.costs),
        unpriced: (r.costs || []).length === 0 && num(r.unpriced_call_cnt) > 0,
        costs: r.costs || [], baseAmt: r.base_cost_amt, baseText: baseCostText(baseCurrency, r),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, def, t, currency, baseCurrency]);

  const totalBasis = currency ? costOf(kpiCosts) : num(k.total_tokens);
  const share = (r) => (totalBasis > 0 ? (r[basisKey] / totalBasis) * 100 : 0);

  const cmp = (a, b) => {
    const d = sortKey === "name" ? a.name.localeCompare(b.name) : (a[sortKey] ?? 0) - (b[sortKey] ?? 0);
    return sortDesc ? -d : d;
  };

  // 사용자 축은 기준값 상위 10명 + 나머지 묶음. 서버가 상위 50명 밖을 이미 묶어 준 행도 여기에 더한다
  const serverOthers = dense ? summary.by_user_others : null;
  const byBasis = [...rows].sort((a, b) => b[basisKey] - a[basisKey]);
  const collapsible = dense && !expanded && rows.length > TOP_N;
  const shown = (collapsible ? byBasis.slice(0, TOP_N) : rows).slice().sort(cmp);
  const rest = collapsible ? byBasis.slice(TOP_N) : [];
  let others = null;
  if (rest.length > 0 || serverOthers) {
    const so = serverOthers || {};
    // 묶인 행 중 하나라도 환산 불가면 묶음도 환산하지 않는다
    const parts = [...rest.map((r) => ({ costs: r.costs, base_cost_amt: r.baseAmt })), ...(serverOthers ? [so] : [])]
      .filter((p) => (p.costs || []).length > 0);
    const baseAcc = {
      costs: parts.flatMap((p) => p.costs),
      base_cost_amt: parts.some((p) => p.base_cost_amt == null) ? null
        : parts.reduce((s, p) => s + num(p.base_cost_amt), 0),
    };
    others = {
      count: rest.length + num(so.user_cnt),
      calls: rest.reduce((s, r) => s + r.calls, 0) + num(so.call_cnt),
      tokens: rest.reduce((s, r) => s + r.tokens, 0) + num(so.total_tokens),
      cost: currency ? rest.reduce((s, r) => s + r.cost, 0) + costOf(so.costs) : null,
      expandable: rest.length > 0,
      baseText: baseCostText(baseCurrency, baseAcc),
    };
  }

  // 머리글에 통화를 두지 않으므로 단일 통화면 금액 앞에 작게 단위를 붙인다
  const withUnit = (v) => (
    <>
      <Box component="span" sx={{ fontSize: FONT.CAPTION, fontWeight: 400, color: UC.text3, mr: 0.5 }}>{currency}</Box>
      {fmtCost(v, currency)}
    </>
  );

  const costCell = (r) => {
    if (r.unpriced) return <Box component="span" sx={{ color: UC.text3 }}>{t("llmUsageUnpriced")}</Box>;
    return <CostStack main={currency ? withUnit(r.cost) : (r.costText || "-")} base={r.baseText} />;
  };

  const headers = [
    { key: "name", label: t(def.headerKey), align: "left" },
    { key: "calls", label: t("llmUsageCalls") },
    { key: "tokens", label: t("llmUsageTotalTokens") },
    { key: "cost", label: t("llmUsageEstCost"), sortable: !!currency },
  ];
  const clickSort = (key) => setSort((s) => (s?.key === key || (!s && key === basisKey)
    ? { key, desc: !sortDesc } : { key, desc: key !== "name" }));

  const pad = "13px";

  return (
    <Box sx={{ ...CARD_SX, p: 0, overflow: "hidden", ...sx }}>
      <Box sx={{ px: "16px", pt: "14px", pb: "10px", flexShrink: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box component="h3" sx={{ m: 0, fontSize: FONT.TITLE, fontWeight: 600, color: UC.text1 }}>{t("llmUsageBreakdown")}</Box>
          <AiSegmentedControl value={def.key} onChange={onChangeAxis} sx={{ flexWrap: "nowrap", flexShrink: 0 }}
            options={BREAKDOWN_AXES.map((a) => ({ value: a.key, label: t(a.labelKey) }))} />
        </Box>
      </Box>

      <Box component="table" role="table" sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
        borderCollapse: "collapse", width: "100%" }}>
        <Box component="thead" role="rowgroup" sx={{ ...SECTION_SX, borderTop: `1px solid ${UC.line}`, borderBottom: `1px solid ${UC.line}` }}>
          <Box component="tr" role="row" sx={{ ...ROW_SX, height: 28 }}>
            {headers.map((h) => {
              const active = sortKey === h.key;
              const sortable = h.sortable !== false;
              return (
                <Box component="th" role="columnheader" scope="col" key={h.key}
                  aria-sort={active ? (sortDesc ? "descending" : "ascending") : "none"}
                  sx={{ p: 0, minWidth: 0, fontWeight: 500, textAlign: h.align || "right" }}>
                  <Box component="button" type="button" disabled={!sortable} onClick={() => clickSort(h.key)}
                    sx={{ all: "unset", boxSizing: "border-box", maxWidth: "100%", display: "inline-flex", alignItems: "center",
                      gap: "2px", cursor: sortable ? "pointer" : "default", fontSize: FONT.CAPTION,
                      color: active ? UC.text1 : UC.text3, "&:focus-visible": { outline: `2px solid ${UC.acc}` } }}>
                    <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{h.label}</Box>
                    {active && <Box component="span" aria-hidden sx={{ fontSize: 8, flexShrink: 0 }}>{sortDesc ? "▼" : "▲"}</Box>}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box component="tbody" role="rowgroup" sx={{ ...SECTION_SX, flex: 1, minHeight: 0, flexShrink: 1, overflowY: "auto" }}>
          {shown.map((r) => (
            <Box component="tr" role="row" key={r.id}
              sx={{ ...ROW_SX, py: pad, borderBottom: `1px solid ${UC.grid}`, "&:hover": { bgcolor: UC.hover } }}>
              <Box component="th" role="rowheader" scope="row" sx={{ p: 0, minWidth: 0, textAlign: "left", fontWeight: 500 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                  <Box sx={{ fontSize: FONT.TITLE, lineHeight: 1.3, color: r.muted ? UC.text3 : UC.text1,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.name}</Box>
                  {r.provider && <ProviderChip provider={r.provider} />}
                </Box>
                <ShareBar pct={share(r)} />
              </Box>
              <Box component="td" role="cell" sx={{ ...CELL_NUM_SX, p: 0, fontSize: FONT.SUB, color: UC.text2 }}>{fmtInt(r.calls)}</Box>
              <Box component="td" role="cell" sx={{ ...CELL_NUM_SX, p: 0, fontSize: FONT.SUB, color: UC.text2 }}>{fmtInt(r.tokens)}</Box>
              <Box component="td" role="cell"
                sx={{ ...CELL_NUM_SX, p: 0, fontSize: FONT.SUB, fontWeight: 500, color: UC.text1 }}>{costCell(r)}</Box>
            </Box>
          ))}
          {others && (
            <Box component="tr" role="row" tabIndex={others.expandable ? 0 : -1}
              aria-expanded={others.expandable ? false : undefined}
              onClick={() => others.expandable && onExpand()}
              onKeyDown={(e) => { if (others.expandable && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onExpand(); } }}
              sx={{ ...ROW_SX, py: pad, cursor: others.expandable ? "pointer" : "default",
                "&:hover": { bgcolor: others.expandable ? UC.hover : "transparent" },
                "&:focus-visible": { outline: `2px solid ${UC.acc}`, outlineOffset: -2 } }}>
              <Box component="th" role="rowheader" scope="row" sx={{ p: 0, minWidth: 0, textAlign: "left", fontWeight: 400 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, fontSize: FONT.TITLE,
                  color: others.expandable ? UC.acc : UC.text3 }}>
                  <Box component="span" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {t("llmUsageOthers", { count: fmtInt(others.count) })}
                  </Box>
                  {others.expandable && <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                </Box>
              </Box>
              <Box component="td" role="cell" sx={{ ...CELL_NUM_SX, p: 0, fontSize: FONT.SUB, color: UC.text3 }}>{fmtInt(others.calls)}</Box>
              <Box component="td" role="cell" sx={{ ...CELL_NUM_SX, p: 0, fontSize: FONT.SUB, color: UC.text3 }}>{fmtInt(others.tokens)}</Box>
              <Box component="td" role="cell" sx={{ ...CELL_NUM_SX, p: 0, fontSize: FONT.SUB, color: UC.text3 }}>
                <CostStack main={currency ? withUnit(others.cost) : "-"} base={others.baseText} />
              </Box>
            </Box>
          )}
        </Box>

        {rows.length > 0 && (
          <Box component="tfoot" role="rowgroup" sx={{ ...SECTION_SX, bgcolor: UC.footer, borderTop: `1px solid ${UC.line}` }}>
            <Box component="tr" role="row" sx={{ ...ROW_SX, py: "10px" }}>
              <Box component="th" role="rowheader" scope="row" sx={{ p: 0, textAlign: "left", fontSize: FONT.TITLE, fontWeight: 600, color: UC.text1 }}>
                {t("sum")}
              </Box>
              <Box component="td" role="cell" sx={{ ...CELL_NUM_SX, p: 0, fontSize: FONT.SUB, color: UC.text2 }}>{fmtInt(k.call_cnt)}</Box>
              <Box component="td" role="cell" sx={{ ...CELL_NUM_SX, p: 0, fontSize: FONT.SUB, color: UC.text2 }}>{fmtInt(k.total_tokens)}</Box>
              <Box component="td" role="cell"
                sx={{ ...CELL_NUM_SX, p: 0, fontSize: FONT.SUB, fontWeight: 700, color: UC.text1 }}>
                <CostStack main={currency ? withUnit(costOf(kpiCosts)) : (costsText(kpiCosts) || "-")}
                  base={baseCostText(baseCurrency, k)} />
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LlmUsageBreakdownCard;
